import { Component, ElementRef, OnInit, ViewChild, inject } from '@angular/core';
import { Store } from '@ngrx/store';
import { Observable } from 'rxjs';

import { ControlType } from './models/control-room-models';
import {
  getAutomaticAdviceCustomersPreferencesLastUpdate,
  getAutomaticFlowSchedulerEnabledState,
  resetAutomaticAdviceForAllCustomers,
  setAutomaticAdviceCustomersPreferences,
  setAutomaticFlowEnabledState,
  showRecalculateBbaAdviceModal,
  triggerAutomaticFlow,
  uploadCompanySettingsFromCsv,
} from './store/control-room.actions';
import { ControlRoomState } from './store/control-room.feature';
import {
  selectEnabledStateByType,
  selectLastAutomaticAdviceCustomersPreferencesUpdate,
  selectLoadingByType,
} from './store/control-room.selectors';

@Component({
  selector: 'app-control-room',
  templateUrl: './control-room.component.html',
  styleUrls: ['./control-room.component.scss'],
  standalone: false,
})
export class ControlRoomComponent implements OnInit {
  private readonly store = inject<Store<ControlRoomState>>(Store);

  @ViewChild('file', { static: true }) inputFile!: ElementRef<HTMLInputElement>;
  selectedFile!: File | null;
  enabledAdviceState$: Observable<boolean>;
  enabledExportState$: Observable<boolean>;
  enabledOrderState$: Observable<boolean>;
  loadingAdvice$: Observable<boolean>;
  loadingExport$: Observable<boolean>;
  loadingOrder$: Observable<boolean>;
  loadingBbaRecalculation$: Observable<boolean>;
  lastAutomaticAdviceCustomersPreferencesUpdate$: Observable<Date | null>;
  automaticSchedulerType = ControlType;

  constructor() {
    this.enabledAdviceState$ = this.store.select(
      selectEnabledStateByType(this.automaticSchedulerType.advice),
    );
    this.enabledExportState$ = this.store.select(
      selectEnabledStateByType(this.automaticSchedulerType.exportFertilizationBalanceDataFertilab),
    );
    this.enabledOrderState$ = this.store.select(
      selectEnabledStateByType(this.automaticSchedulerType.order),
    );
    this.loadingAdvice$ = this.store.select(
      selectLoadingByType(this.automaticSchedulerType.advice),
    );
    this.loadingExport$ = this.store.select(
      selectLoadingByType(this.automaticSchedulerType.exportFertilizationBalanceDataFertilab),
    );
    this.loadingOrder$ = this.store.select(selectLoadingByType(this.automaticSchedulerType.order));
    this.loadingBbaRecalculation$ = this.store.select(
      selectLoadingByType(ControlType.bbaRecalculation),
    );

    this.lastAutomaticAdviceCustomersPreferencesUpdate$ = this.store.select(
      selectLastAutomaticAdviceCustomersPreferencesUpdate,
    );
  }

  ngOnInit(): void {
    this.store.dispatch(
      getAutomaticFlowSchedulerEnabledState({ type: this.automaticSchedulerType.advice }),
    );
    this.store.dispatch(
      getAutomaticFlowSchedulerEnabledState({
        type: this.automaticSchedulerType.exportFertilizationBalanceDataFertilab,
      }),
    );
    this.store.dispatch(
      getAutomaticFlowSchedulerEnabledState({ type: this.automaticSchedulerType.order }),
    );
    this.store.dispatch(getAutomaticAdviceCustomersPreferencesLastUpdate());
  }

  triggerAutomaticAdvice(): void {
    this.store.dispatch(triggerAutomaticFlow({ type: this.automaticSchedulerType.advice }));
  }

  triggerAutomaticCropFieldExport(): void {
    this.store.dispatch(
      triggerAutomaticFlow({
        type: this.automaticSchedulerType.exportFertilizationBalanceDataFertilab,
      }),
    );
  }

  triggerAutomaticCropFieldOrder(): void {
    this.store.dispatch(triggerAutomaticFlow({ type: this.automaticSchedulerType.order }));
  }

  activateScheduler(type: ControlType): void {
    const request = { enabled: true, type };
    this.store.dispatch(setAutomaticFlowEnabledState(request));
  }

  disableScheduler(type: ControlType): void {
    const request = { enabled: false, type };
    this.store.dispatch(setAutomaticFlowEnabledState(request));
  }

  onFileSelected() {
    this.selectedFile = this.inputFile?.nativeElement?.files
      ? this.inputFile?.nativeElement?.files[0]
      : null;
  }

  automaticAdvice() {
    if (this.selectedFile) {
      this.store.dispatch(uploadCompanySettingsFromCsv({ file: this.selectedFile }));
    }
  }

  triggerBbaRecalculation(): void {
    this.store.dispatch(showRecalculateBbaAdviceModal());
  }

  setAutomaticAdviceCustomersPreferences(): void {
    this.store.dispatch(setAutomaticAdviceCustomersPreferences());
  }

  resetAutomaticAdviceForAllCustomers(): void {
    this.store.dispatch(resetAutomaticAdviceForAllCustomers());
  }
}
